import { getAuditScoreColorVar, getAuditScoreTextClass } from './scoreBands';

/**
 * Half-circle gauge component for displaying scores
 * Inspired by MUI X Gauge - renders a semicircular arc with score in center-bottom
 */
export function ScoreGauge({
  score,
  size = 'medium',
}: {
  score: number;
  size?: 'small' | 'medium' | 'large' | 'xlarge';
}) {
  // Size configurations
  const sizeConfig = {
    small: {
      width: 48,
      height: 28,
      strokeWidth: 6,
      fontSize: 'text-sm',
      fontWeight: 'font-bold',
    },
    medium: {
      width: 120,
      height: 70,
      strokeWidth: 10,
      fontSize: 'text-2xl',
      fontWeight: 'font-bold',
    },
    large: {
      width: 160,
      height: 90,
      strokeWidth: 12,
      fontSize: 'text-3xl',
      fontWeight: 'font-black',
    },
    xlarge: {
      width: 214,
      height: 120,
      strokeWidth: 16,
      fontSize: 'text-4xl',
      fontWeight: 'font-black',
    },
  };

  const config = sizeConfig[size];
  const { width, height, strokeWidth } = config;
  const roundedScore = Math.round(Math.max(0, Math.min(100, score)));

  // Calculate arc parameters
  const radius = (width - strokeWidth) / 2;
  const centerX = width / 2;
  const centerY = height - 4; // Position arc from bottom

  // Arc path: semicircle from left to right
  const startAngle = Math.PI; // 180 degrees (left)
  const scoreAngle = startAngle - (roundedScore / 100) * Math.PI; // Map score to angle

  // SVG arc path calculation
  const polarToCartesian = (cx: number, cy: number, r: number, angle: number) => ({
    x: cx + r * Math.cos(angle),
    y: cy - r * Math.sin(angle),
  });

  const startPoint = polarToCartesian(centerX, centerY, radius, startAngle);
  const endPoint = polarToCartesian(centerX, centerY, radius, 0);
  const scorePoint = polarToCartesian(centerX, centerY, radius, scoreAngle);

  // Background arc (full semicircle)
  const bgArcPath = `M ${startPoint.x} ${startPoint.y} A ${radius} ${radius} 0 0 1 ${endPoint.x} ${endPoint.y}`;

  // Score arc (partial, based on score)
  // For a semicircle gauge (180° span), the arc never exceeds 180°, so largeArcFlag is always 0
  const scoreArcPath = `M ${startPoint.x} ${startPoint.y} A ${radius} ${radius} 0 0 1 ${scorePoint.x} ${scorePoint.y}`;

  const strokeColor = getAuditScoreColorVar(roundedScore, 'var(--color-sev-critical)');
  const textClass = getAuditScoreTextClass(roundedScore, 'text-destructive');

  return (
    <div
      className="relative inline-flex max-w-full flex-col items-center overflow-visible"
      style={{ width, maxWidth: '100%' }}
    >
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        className="h-auto max-w-full overflow-visible"
      >
        {/* Background arc */}
        <path
          d={bgArcPath}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          className="text-gray-200 dark:text-zinc-700"
        />
        {/* Score arc */}
        <path
          d={scoreArcPath}
          fill="none"
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
      </svg>
      {/* Score value at center-bottom */}
      <div
        className={`absolute bottom-0 font-accent ${config.fontSize} ${config.fontWeight} ${textClass}`}
      >
        {roundedScore}
      </div>
    </div>
  );
}
